import React from 'react';

// Skeleton pulse animation component
const SkeletonPulse = ({ className }) => (
  <div className={`animate-pulse bg-neutral-200 rounded ${className}`} />
);

// Dashboard metrics skeleton
export function MetricsSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="rounded-2xl bg-white/60 backdrop-blur-sm border border-white/40 p-4">
          <SkeletonPulse className="h-4 w-20 mb-2 rounded-lg" />
          <SkeletonPulse className="h-8 w-12 rounded-lg" />
        </div>
      ))}
    </div>
  );
}

// Quick actions skeleton
export function QuickActionsSkeleton() {
  return (
    <div className="mb-8">
      <SkeletonPulse className="h-6 w-32 mb-4 rounded-lg" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="rounded-2xl bg-white/60 backdrop-blur-sm border border-white/40 p-6 flex flex-col items-center">
            <SkeletonPulse className="h-12 w-12 rounded-xl mb-3" />
            <SkeletonPulse className="h-4 w-20 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  );
}

// Task list skeleton
export function TaskListSkeleton({ rows = 5 }) {
  return (
    <div className="bg-white rounded-xl border border-white/30 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-white/30 flex justify-between items-center">
        <SkeletonPulse className="h-5 w-24" />
        <SkeletonPulse className="h-8 w-32 rounded-xl" />
      </div>
      
      {/* Table header */}
      <div className="px-4 py-3 bg-white/40 border-b border-white/30 grid grid-cols-6 gap-4">
        {[...Array(6)].map((_, i) => (
          <SkeletonPulse key={i} className="h-4 w-full" />
        ))}
      </div>
      
      {/* Table rows */}
      {[...Array(rows)].map((_, i) => (
        <div key={i} className="px-4 py-4 border-b border-gray-50 grid grid-cols-6 gap-4 items-center">
          <SkeletonPulse className="h-4 w-full" />
          <SkeletonPulse className="h-4 w-3/4" />
          <SkeletonPulse className="h-4 w-20" />
          <SkeletonPulse className="h-4 w-24" />
          <SkeletonPulse className="h-6 w-16 rounded-full" />
          <SkeletonPulse className="h-6 w-14 rounded-full" />
        </div>
      ))}
    </div>
  );
}

// Full dashboard skeleton
export function DashboardSkeleton() {
  return (
    <div className="p-6 animate-in fade-in duration-300">
      {/* Header */}
      <div className="mb-6">
        <SkeletonPulse className="h-8 w-48 mb-2" />
        <SkeletonPulse className="h-4 w-64" />
      </div>
      
      {/* Metrics */}
      <MetricsSkeleton />
      
      {/* Second row metrics */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="bg-white rounded-xl border border-white/30 p-4">
            <SkeletonPulse className="h-4 w-16 mb-2" />
            <SkeletonPulse className="h-8 w-10" />
          </div>
        ))}
      </div>
      
      {/* Quick Actions */}
      <QuickActionsSkeleton />
      
      {/* Recent Activity placeholder */}
      <div className="bg-white rounded-xl border border-white/30 p-4">
        <SkeletonPulse className="h-5 w-32 mb-4" />
        {[...Array(4)].map((_, i) => (
          <div key={i} className="flex items-center gap-3 py-3 border-b border-gray-50 last:border-0">
            <SkeletonPulse className="h-8 w-8 rounded-full" />
            <div className="flex-1">
              <SkeletonPulse className="h-4 w-3/4 mb-1" />
              <SkeletonPulse className="h-3 w-20" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Sidebar skeleton for menus
export function SidebarSkeleton() {
  return (
    <div className="p-4 space-y-2">
      {[...Array(10)].map((_, i) => (
        <div key={i} className="flex items-center gap-3 p-3 rounded-xl">
          <SkeletonPulse className="h-5 w-5 rounded" />
          <SkeletonPulse className="h-4 w-24" />
        </div>
      ))}
    </div>
  );
}

// Card skeleton for various uses
export function CardSkeleton({ lines = 3 }) {
  return (
    <div className="bg-white rounded-xl border border-white/30 p-4">
      <SkeletonPulse className="h-5 w-32 mb-3" />
      {[...Array(lines)].map((_, i) => (
        <SkeletonPulse key={i} className="h-4 w-full mb-2" />
      ))}
    </div>
  );
}

export default DashboardSkeleton;
